import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { InfoBlock, ListTable } from './DetailParts';
import { nvdUrl, severityColor, severityMix } from './cveSummary';
import { hasUsageData, unusedKeySet, unusedPrograms, usageIndex, usageOf } from './cveUsage';

const asList = (v) => (Array.isArray(v) ? v : []);

// A name list inside a collapsed section (assessed clean, not in the CVE data).
function NameList({ title, names, note }) {
  return (
    <Accordion variant="outlined" disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title} ({names.length})</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {note && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{note}</Typography>}
        {names.length === 0
          ? <Typography variant="body2" color="text.secondary">None.</Typography>
          : (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {names.map((n, i) => <Chip key={`${n}-${i}`} size="small" variant="outlined" label={n} />)}
            </Stack>
          )}
      </AccordionDetails>
    </Accordion>
  );
}

// One CVE as a chip that opens its National Vulnerability Database page.
function CveChip({ match }) {
  const url = nvdUrl(match.cve);
  const score = match.score === null || typeof match.score === 'undefined' ? '-' : match.score;
  const label = `${match.cve} (${score})`;
  const props = url
    ? { component: 'a', href: url, target: '_blank', rel: 'noopener noreferrer', clickable: true }
    : {};
  return (
    <Chip
      size="small"
      color={severityColor(match.severity)}
      variant="outlined"
      label={label}
      title={[match.severity || 'Unscored', match.confidence ? `${match.confidence} confidence` : ''].filter(Boolean).join(', ')}
      {...props}
    />
  );
}

// When the program last ran, and what to do about its CVEs: unused ones are "remove or update".
function UsageCell({ info }) {
  return (
    <TableCell title={info.source || undefined}>
      <Typography variant="body2">{info.label}</Typography>
      {info.detail && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{info.detail}</Typography>}
      {info.decision === 'unused' && <Chip size="small" color="warning" label="Remove or update" sx={{ mt: 0.5 }} />}
      {info.decision === 'in-use' && <Chip size="small" variant="outlined" label="In use: update" sx={{ mt: 0.5 }} />}
    </TableCell>
  );
}

// The machine's known CVEs (looked up by the API from its installed programs): what is known, what could not be judged, and what is not in
// the CVE data. "No CVEs" is never presented as "safe": the programs that cannot be assessed are listed.
function CveDetail({ data }) {
  const cve = data?.cve;
  if (!cve) {
    return (
      <Typography variant="body2" color="text.secondary">
        The known CVEs are not available for this machine yet.
      </Typography>
    );
  }
  if (cve.status === 'loading') {
    return <Typography variant="body2" color="text.secondary">{cve.message || 'Looking up the known CVEs...'}</Typography>;
  }
  if (cve.status === 'skipped') {
    return (
      <Typography variant="body2" color="text.secondary">
        The CVE lookup was skipped{cve.message ? `: ${cve.message}` : '.'}
      </Typography>
    );
  }
  if (cve.status === 'error') {
    return (
      <Typography variant="body2" color="error.main">
        The CVE lookup failed{cve.httpStatus ? ` (HTTP ${cve.httpStatus})` : ''}: {cve.message || 'unknown error'}
      </Typography>
    );
  }
  const counts = cve.counts || {};
  const vulnerable = asList(cve.vulnerablePrograms);
  const sev = cve.uniqueCvesBySeverity || {};

  // Run history: when the script reports it, each program shows when it last ran, and the ones that have CVEs
  // but are not in use are called out (a remove-or-update decision).
  const showUsage = hasUsageData(data);
  const usage = usageIndex(data);
  const unusedKeys = unusedKeySet(data);
  const unused = showUsage ? unusedPrograms(data) : [];

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <InfoBlock
            title="Known vulnerabilities"
            rows={[
              ['Computer', data.computerName],
              ['Programs with known CVEs', counts.vulnerable],
              ['Unique CVEs', cve.uniqueCves],
              ['By severity', severityMix(sev)],
              ['Not in use, with CVEs', showUsage ? unused.length : null],
              ['Minimum confidence', cve.minConfidence],
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InfoBlock
            title="Everything else on the machine"
            rows={[
              ['Checked, no CVEs apply', counts.assessedClean],
              ['Cannot assess', counts.cannotAssess],
              ['Only low-confidence matches', counts.lowConfidenceOnly],
              ['Not in the CVE data', counts.notInDataset],
              ['Skipped (Store components)', cve.skippedCount],
            ]}
          />
        </Grid>
      </Grid>

      {cve.trimmed && (
        <Typography variant="caption" color="text.secondary">
          The result was shortened to stay within the size limit: fewer CVEs are listed per program.
        </Typography>
      )}

      {unused.length > 0 && (
        <Alert severity="warning" variant="outlined">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Not in use: remove or update ({unused.length})
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            These programs have known CVEs and have not been run recently. If nobody needs them, uninstalling
            closes all of their CVEs; if they are needed, update them.
          </Typography>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {unused.map(p => (
              <Chip
                key={`${p.name}-${p.version}`}
                size="small"
                color="warning"
                variant="outlined"
                label={`${p.name} · ${p.cveCount} CVEs · ${p.lastRunDate ? `last run ${p.lastRunDate}` : 'not seen recently'}`}
              />
            ))}
          </Stack>
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
          Programs with known CVEs ({vulnerable.length})
        </Typography>
        {vulnerable.length === 0 ? (
          <Typography variant="body2" color="text.secondary">None.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Program</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Version</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">CVEs</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Worst score</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Severity</TableCell>
                  {showUsage && <TableCell sx={{ fontWeight: 700 }}>Last run</TableCell>}
                  <TableCell sx={{ fontWeight: 700 }}>Highest scoring CVEs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vulnerable.map((p, i) => (
                  <TableRow key={`${p.name}-${p.version}-${i}`} hover>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{p.version}</TableCell>
                    <TableCell align="right">{p.cveCount}</TableCell>
                    <TableCell align="right">{p.highestScore}</TableCell>
                    <TableCell>{severityMix(p.bySeverity)}</TableCell>
                    {showUsage && <UsageCell info={usageOf(p, usage, unusedKeys)} />}
                    <TableCell>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {asList(p.matches).map(m => <CveChip key={m.cve} match={m} />)}
                        {p.cveCount > asList(p.matches).length && (
                          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                            + {p.cveCount - asList(p.matches).length} more
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <ListTable
        title="Cannot assess"
        columns={[
          { key: 'name', label: 'Program' },
          { key: 'version', label: 'Version' },
          { key: 'unassessableCves', label: 'CVE records that cannot be compared' },
          { key: 'assessableCves', label: 'CVE records that could be' },
        ]}
        rows={asList(cve.cannotAssess)}
      />
      {asList(cve.cannotAssess).length > 0 && cve.cannotAssessNote && (
        <Typography variant="caption" color="text.secondary">{cve.cannotAssessNote}</Typography>
      )}

      <ListTable
        title="Only low-confidence matches (not counted above)"
        columns={[
          { key: 'name', label: 'Program' },
          { key: 'lowConfidenceCves', label: 'Possible CVEs' },
        ]}
        rows={asList(cve.lowConfidenceOnly)}
      />

      <NameList title="Checked, no CVEs apply" names={asList(cve.assessedClean)} />
      <NameList
        title="Not in the CVE data"
        names={asList(cve.notInDataset)}
        note="No CVE record uses these names. That can mean nothing is known, or the name could not be matched."
      />
    </Stack>
  );
}

export default CveDetail;
